"use client";

import { useState, useCallback } from "react";

export interface ContactFormValues {
  name: string;
  email: string;
  reason: string;
  message: string;
}

type Status = "idle" | "submitting" | "success" | "error";

const EMPTY_VALUES: ContactFormValues = {
  name: "",
  email: "",
  reason: "",
  message: "",
};

function validate(values: ContactFormValues) {
  const errors: Partial<Record<keyof ContactFormValues, string>> = {};
  if (!values.name.trim()) errors.name = "Please enter your name.";
  if (!values.email.trim()) {
    errors.email = "Please enter your email.";
  } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(values.email)) {
    errors.email = "Enter a valid email address.";
  }
  if (!values.message.trim()) {
    errors.message = "Please enter a message.";
  } else if (values.message.length > 500) {
    errors.message = "Message must be under 500 characters.";
  }
  return errors;
}

export function useContactForm() {
  const [values, setValues] = useState<ContactFormValues>(EMPTY_VALUES);
  const [touched, setTouched] = useState<Partial<Record<keyof ContactFormValues, boolean>>>({});
  const [status, setStatus] = useState<Status>("idle");

  const errors = validate(values);
  const visibleErrors =
    status === "error" || status === "submitting"
      ? errors
      : Object.fromEntries(
        Object.entries(errors).filter(([key]) => touched[key as keyof ContactFormValues])
      );

  const setField = useCallback((field: keyof ContactFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setTouchedField = useCallback((field: keyof ContactFormValues) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const reset = useCallback(() => {
    setValues(EMPTY_VALUES);
    setTouched({});
    setStatus("idle");
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const currentErrors = validate(values);
      if (Object.keys(currentErrors).length > 0) {
        setTouched({ name: true, email: true, reason: true, message: true });
        setStatus("error");
        return;
      }

      setStatus("submitting");
      try {
        const res = await fetch("/api/forms/generalContact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });

        if (!res.ok) {
          const result = await res.json().catch(() => ({}));
          throw new Error(result.error || "Failed to send message.");
        }

        setStatus("success");
      } catch {
        setStatus("error");
      }
    },
    [values]
  );

  return { values, errors: visibleErrors, status, setField, setTouchedField, handleSubmit, reset };
}